import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, useOpenClose, Button, ButtonTooltip, LinkButton } from '@evertrue/et-components';
import { mostRecentCreate } from './enrichment-utils';
import ProfileCardHeader from 'apps/profile/components/profile-card-header';
import OtherDetailsTabs from './other-details-tabs';
import NoteworthyEventsTable from './noteworthy-events-table';
import PersonalDetailsSection from './personal-details-section';
import OtherDetailsModal from './other-details-modal';

const OtherDetailsCard = ({ contact, enrichmentData, onDelete }) => {
  const {
    wealth_life_events: wealthLifeEvents = [],
    employment_events: employmentEvents = [],
    enrichment_attributes: enrichmentAttributes = [],
    contact_locations: contactLocations = [],
  } = enrichmentData;

  // Filter active (non-rejected) items
  // Note: Events don't have rejected field in API, so use all events
  const activeWealthEvents = wealthLifeEvents || [];
  const activeEmploymentEvents = employmentEvents || [];
  const activeNames = enrichmentAttributes.filter((name) => !name.rejected);
  const activeLocations = contactLocations.filter((location) => !location.rejected);

  // Combine all events
  const allActiveEvents = [...activeWealthEvents, ...activeEmploymentEvents];

  // Tab state management
  const [activeTab, setActiveTab] = useState('noteworthy');
  const [isModalOpen, openModal, closeModal] = useOpenClose();

  // Determine which tabs to show
  const tabs = [];
  if (allActiveEvents.length > 0) {
    tabs.push({ key: 'noteworthy', label: 'NOTEWORTHY EVENTS' });
  }
  // Personal Details tab only shown if there's name or location data
  if (activeNames.length > 0 || activeLocations.length > 0) {
    tabs.push({ key: 'personal', label: 'PERSONAL DETAILS' });
  }

  // Set default active tab and handle tab switching when data changes
  useEffect(() => {
    const hasNoteworthyData = allActiveEvents.length > 0;
    const hasPersonalData = activeNames.length > 0 || activeLocations.length > 0;

    // If current tab no longer has data, switch to the tab that does
    if (activeTab === 'personal' && !hasPersonalData && hasNoteworthyData) {
      setActiveTab('noteworthy');
    } else if (activeTab === 'noteworthy' && !hasNoteworthyData && hasPersonalData) {
      setActiveTab('personal');
    } else if (!activeTab || (activeTab === 'noteworthy' && !hasNoteworthyData)) {
      // Initial load or noteworthy tab is empty
      if (hasNoteworthyData) {
        setActiveTab('noteworthy');
      } else if (hasPersonalData) {
        setActiveTab('personal');
      }
    } else if (activeTab === 'personal' && !hasPersonalData) {
      // Personal tab is empty
      if (hasPersonalData) {
        setActiveTab('personal');
      } else if (hasNoteworthyData) {
        setActiveTab('noteworthy');
      }
    }
  }, [allActiveEvents.length, activeNames.length, activeLocations.length, activeTab]);

  // Calculate additional items count based on active tab
  const noteworthyAdditionalCount = Math.max(allActiveEvents.length - 3, 0);
  const personalAdditionalCount = Math.max(activeNames.length - 1, 0) + Math.max(activeLocations.length - 1, 0);
  const additionalCount = activeTab === 'noteworthy' ? noteworthyAdditionalCount : personalAdditionalCount;

  // Calculate last update date
  const allData = [...wealthLifeEvents, ...employmentEvents, ...enrichmentAttributes, ...contactLocations];
  const lastUpdateDate = allData.length > 0 ? mostRecentCreate(allData, 'updated_at', 'MMM DD, YYYY') : null;

  // Show edit icon if there's ANY data (active or rejected) - allows access to restore deleted items
  const hasData = allData.length > 0;

  return (
    <>
      <ProfileCardHeader title="Other Details" updateDate={lastUpdateDate} showDateAsTooltip={true}>
        {hasData && (
          <Button type="simple" onClick={isModalOpen ? closeModal : openModal}>
            <Icon title="Edit Other Details" icon="edit" />
          </Button>
        )}
      </ProfileCardHeader>

      {/* Tabs */}
      {tabs.length > 0 && <OtherDetailsTabs tabs={tabs} activeTab={activeTab} onTabClick={setActiveTab} />}

      {/* Body content */}
      <div className="profile--enrichment-inner">
        {tabs.length === 0 ? (
          <div className="empty-state-message">
            <p className="empty-state-message--text">No enriched data available for this constituent</p>
          </div>
        ) : (
          <>
            {activeTab === 'noteworthy' && <NoteworthyEventsTable events={allActiveEvents} />}
            {activeTab === 'personal' && <PersonalDetailsSection names={activeNames} locations={activeLocations} />}
          </>
        )}
      </div>

      {/* Footer with "more" button */}
      {additionalCount > 0 && (
        <div className="profile--enrichment-footer">
          <ButtonTooltip
            title={
              activeTab === 'noteworthy'
                ? `View ${additionalCount} more noteworthy event${additionalCount > 1 ? 's' : ''}`
                : `View ${additionalCount} more detail${additionalCount > 1 ? 's' : ''}`
            }
            trigger={
              <LinkButton onClick={isModalOpen ? closeModal : openModal} className="profile--enrichment-more-btn">
                (+{additionalCount} more)
              </LinkButton>
            }
          >
            <div className="centered-tooltip">
              {activeTab === 'noteworthy'
                ? `View ${additionalCount} more noteworthy event${additionalCount > 1 ? 's' : ''}`
                : `View ${additionalCount} more detail${additionalCount > 1 ? 's' : ''}`}
            </div>
          </ButtonTooltip>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <OtherDetailsModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          contact={contact}
          enrichmentData={enrichmentData}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

OtherDetailsCard.propTypes = {
  contact: PropTypes.object.isRequired,
  enrichmentData: PropTypes.shape({
    wealth_life_events: PropTypes.array,
    employment_events: PropTypes.array,
    enrichment_attributes: PropTypes.array,
    contact_locations: PropTypes.array,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default OtherDetailsCard;
